import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import SuccessNotification from './SuccessNotification';

const AdminUserDetail = ({ adminId, userId, onBack }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  
  // Success notification state
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${userId}?admin_id=${adminId}`
      );
      if (!response.ok) throw new Error('Failed to fetch user details');
      const data = await response.json();
      setUserData(data);
      setEditData({
        name: data.user.name,
        email: data.user.email,
        industry: data.user.industry,
        subscription_tier: data.user.subscription_tier,
        subscription_status: data.user.subscription_status
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${userId}?admin_id=${adminId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editData)
        }
      );

      if (!response.ok) throw new Error('Failed to update user');
      
      setNotification({
        show: true,
        message: 'User updated successfully',
        type: 'success'
      });
      setEditMode(false);
      fetchUserDetails();
    } catch (err) {
      setNotification({
        show: true,
        message: `Error: ${err.message}`,
        type: 'error'
      });
    }
  };

  const handleChangePlan = (newPlan) => {
    setConfirmModal({
      isOpen: true,
      title: 'Change Subscription Plan',
      message: `Change subscription to ${newPlan.toUpperCase()}?\n\nThis will update the user's limits and features.`,
      onConfirm: () => confirmChangePlan(newPlan),
      type: 'warning'
    });
  };

  const confirmChangePlan = async (newPlan) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${userId}/subscription?admin_id=${adminId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription_tier: newPlan })
        }
      );

      if (!response.ok) throw new Error('Failed to update subscription');
      
      setNotification({
        show: true,
        message: 'Subscription updated successfully',
        type: 'success'
      });
      fetchUserDetails();
    } catch (err) {
      setNotification({
        show: true,
        message: `Error: ${err.message}`,
        type: 'error'
      });
    }
  };

  const handleResetUsage = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Usage Counters',
      message: 'Reset all usage counters for this user?\n\nThis will set AI images used, posts used, and videos used back to 0.',
      onConfirm: confirmResetUsage,
      type: 'warning'
    });
  };

  const confirmResetUsage = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${userId}/reset-usage?admin_id=${adminId}`,
        { method: 'POST' }
      );

      if (!response.ok) throw new Error('Failed to reset usage');
      
      setNotification({
        show: true,
        message: 'Usage counters reset successfully',
        type: 'success'
      });
      fetchUserDetails();
    } catch (err) {
      setNotification({
        show: true,
        message: `Error: ${err.message}`,
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-orange-500 hover:text-orange-400">
          ← Back to Users
        </button>
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  const { user, posts_count, recent_posts } = userData;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-orange-500 hover:text-orange-400 font-medium"
        >
          ← Back to Users
        </button>
        <div className="flex gap-2">
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Edit User
            </button>
          ) : (
            <>
              <button
                onClick={() => setEditMode(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {editMode ? (
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  className="bg-gray-700 px-3 py-1 rounded border border-gray-600 focus:outline-none focus:border-orange-500"
                />
              ) : (
                user.name
              )}
            </h2>
            <p className="text-gray-400">
              {editMode ? (
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                  className="bg-gray-700 px-3 py-1 rounded border border-gray-600 focus:outline-none focus:border-orange-500 mt-1"
                />
              ) : (
                user.email
              )}
            </p>
          </div>
          {user.role === 'admin' && (
            <span className="bg-orange-600 px-3 py-1 rounded text-sm font-medium">
              ADMIN
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-400">Industry</p>
            {editMode ? (
              <input
                type="text"
                value={editData.industry}
                onChange={(e) => setEditData({...editData, industry: e.target.value})}
                className="mt-1 w-full bg-gray-700 px-2 py-1 rounded border border-gray-600 focus:outline-none focus:border-orange-500 text-sm"
              />
            ) : (
              <p className="font-medium">{user.industry}</p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-400">User ID</p>
            <p className="font-medium text-xs">{user.id.slice(0, 8)}...</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Joined</p>
            <p className="font-medium text-sm">{formatDate(user.created_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Total Posts</p>
            <p className="font-medium">{posts_count}</p>
          </div>
        </div>
      </div>

      {/* Subscription Management */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold mb-4">Subscription Management</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-400 mb-1">Current Plan</p>
            <span className={`inline-block px-3 py-1 rounded font-medium ${
              user.subscription_tier === 'trial' ? 'bg-gray-600' :
              user.subscription_tier === 'basic' ? 'bg-blue-600' :
              user.subscription_tier === 'pro' ? 'bg-purple-600' :
              'bg-orange-600'
            }`}>
              {user.subscription_tier.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">Status</p>
            <span className={`inline-block px-3 py-1 rounded font-medium ${
              user.subscription_status === 'active' ? 'bg-green-600' : 'bg-red-600'
            }`}>
              {user.subscription_status.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <p className="text-sm text-gray-400 w-full mb-2">Change Plan:</p>
          {['trial', 'basic', 'pro', 'enterprise'].map(plan => (
            <button
              key={plan}
              onClick={() => handleChangePlan(plan)}
              disabled={user.subscription_tier === plan}
              className={`px-4 py-2 rounded transition-colors ${
                user.subscription_tier === plan
                  ? 'bg-gray-700 cursor-not-allowed opacity-50'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              {plan.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Usage Statistics</h3>
          <button
            onClick={handleResetUsage}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-sm transition-colors"
          >
            Reset Usage
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">🖼️ AI Images</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{user.ai_images_used}</span>
              <span className="text-gray-400">/ {user.ai_images_limit === -1 ? '∞' : user.ai_images_limit}</span>
            </div>
            {user.ai_images_limit !== -1 && (
              <div className="mt-2 bg-gray-600 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-orange-500 h-full"
                  style={{
                    width: `${Math.min(100, (user.ai_images_used / user.ai_images_limit) * 100)}%`
                  }}
                />
              </div>
            )}
          </div>

          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">📝 Posts Created</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{user.posts_used}</span>
              <span className="text-gray-400">/ {user.posts_limit === -1 ? '∞' : user.posts_limit}</span>
            </div>
            {user.posts_limit !== -1 && (
              <div className="mt-2 bg-gray-600 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-500 h-full"
                  style={{
                    width: `${Math.min(100, (user.posts_used / user.posts_limit) * 100)}%`
                  }}
                />
              </div>
            )}
          </div>

          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">🎥 Videos Generated</p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{user.ai_videos_used}</span>
              <span className="text-gray-400">/ {user.ai_videos_limit === -1 ? '∞' : user.ai_videos_limit}</span>
            </div>
            {user.ai_videos_limit !== -1 && user.ai_videos_limit > 0 && (
              <div className="mt-2 bg-gray-600 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-purple-500 h-full"
                  style={{
                    width: `${Math.min(100, (user.ai_videos_used / user.ai_videos_limit) * 100)}%`
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Posts */}
      {recent_posts.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold mb-4">Recent Posts</h3>
          <div className="space-y-3">
            {recent_posts.map((post) => (
              <div key={post.id} className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-white mb-2">{post.content}</p>
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span>Platform: {post.platform}</span>
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
        type={confirmModal.type}
      />

      {/* Success Notification */}
      {notification.show && (
        <SuccessNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ ...notification, show: false })}
        />
      )}
    </div>
  );
};

export default AdminUserDetail;
